import queryString from 'qs'
import Moment from 'moment'
import mapValues from 'lodash/mapValues'
import pick from 'lodash/pick'
import omitBy from 'lodash/omitBy'
import isNil from 'lodash/isNil'

const filtersWhitelist = [
  'type',
  'country',
  'state',
  'regions',
  'start_date',
  'end_date',
  'startDate',
  'endDate',
  'weekendMonths',
  'priceStart',
  'priceEnd',
  'petFriendly',
  'pets',
  'subcategories',
  'amenities',
  'adults',
  'children',
  'ne',
  'sw',
  'location',
  'origin',
  'adventureTypes',
  'bedrooms',
  'beds',
  'instantBook',
  'fullRefund',
  'oneNightStayWeekday',
  'oneNightStayWeekend',
] as const

const arrayFilters = new Set([
  'regions',
  'subcategories',
  'amenities',
  'adventureTypes',
  'weekendMonths',
  // GeoPoint values are passed as [lat, lng] tuples
  'ne',
  'sw',
  'origin',
])

// Keep in sync with GQL_ListingTypes / GQL_Countries in graphql.d.ts.
// The array literals are typed against the GraphQL enums so removed values
// surface as a tsc error; the Sets stay String-typed so .has() can take any
// string from the URL.
const listingTypes: GQL_ListingTypes[] = [
  'accommodation',
  'adventures',
  'experiences',
  'motorVehicle',
  'stories',
]
const validListingTypes = new Set<string>(listingTypes)

const countries: GQL_Countries[] = ['AU', 'NZ', 'US']
const validCountries = new Set<string>(countries)

const dateFilters = ['startDate', 'endDate', 'start_date', 'end_date'] as const

const numericFilters = [
  'adults',
  'children',
  'bedrooms',
  'beds',
  'priceStart',
  'priceEnd',
] as const

function isValidIsoDate(value: unknown): boolean {
  return (
    typeof value === 'string' && Moment(value, 'YYYY-MM-DD', true).isValid()
  )
}

/**
 * Parse numbers and booleans
 */
function parseValues(value: string) {
  let newValue: string | number | string[] | boolean = value
  if (
    value &&
    !Number.isNaN(Number(value)) &&
    typeof value === 'string' &&
    value.trim() !== ''
  ) {
    newValue = Number(value)
  } else if (
    value &&
    value !== null &&
    typeof value === 'string' &&
    value.trim() !== '' &&
    (value.toLowerCase() === 'true' || value.toLowerCase() === 'false')
  ) {
    newValue = value.toLowerCase() === 'true'
  }

  return newValue
}

type FilterState = GQL_SearchFilters & {
  start_date?: string
  end_date?: string
}

/**
 * We need to serialize a given state into
 * a proper url query string
 */
export function serializeSearchState({
  start_date, // legacy for bucket list
  end_date, // legacy for bucket list
  startDate,
  endDate,
  adults,
  children,
  petFriendly,
  priceStart,
  priceEnd,
  bedrooms,
  beds,
  instantBook,
  fullRefund,
  oneNightStayWeekday,
  oneNightStayWeekend,
  ...state
}: FilterState) {
  const mappedState = {
    ...state,
    petFriendly: petFriendly || null,
    instantBook: instantBook || null,
    fullRefund: fullRefund || null,
    oneNightStayWeekday: oneNightStayWeekday || null,
    oneNightStayWeekend: oneNightStayWeekend || null,
    adults: adults > 0 ? adults : null,
    children: children > 0 ? children : null,
    bedrooms: bedrooms > 0 ? bedrooms : null,
    beds: beds > 0 ? beds : null,
    start_date: start_date ? Moment(start_date).format('YYYY-MM-DD') : null,
    end_date: end_date ? Moment(end_date).format('YYYY-MM-DD') : null,
    startDate: startDate ? Moment(startDate).format('YYYY-MM-DD') : null,
    endDate: endDate ? Moment(endDate).format('YYYY-MM-DD') : null,
    priceStart,
    priceEnd,
  }
  return queryString.stringify(mappedState, {
    arrayFormat: 'brackets',
    encode: false,
    skipNulls: true,
  })
}

/**
 * We need to deserialize a given query string into
 * a plain javascript object
 */
export function deserializeSearchQuery(query: string) {
  let parsed = queryString.parse(query, {
    ignoreQueryPrefix: true,
    // qs converts arrays to objects when the index exceeds arrayLimit (default
    // 20). Subcategory filters can exceed that, so raise the cap to keep them
    // as arrays for GraphQL coercion.
    arrayLimit: 200,
  })
  // Normalise duplicate query keys: for single-value filters, take the first
  // element if the parsed value is an array. Without this, malformed URLs
  // (e.g. `?type=a&type=b`) cause GraphQL coercion errors downstream.
  parsed = mapValues(parsed, (value, key) => {
    if (arrayFilters.has(key) || !Array.isArray(value)) return value
    return value[0]
  })
  parsed = {
    ...parsed,
    state:
      parsed.state !== 'all' && typeof parsed.state === 'string'
        ? parsed.state
        : null, // ignore 'all'
  }
  parsed = pick(parsed, filtersWhitelist) // only whitelisted filters
  // @ts-ignore
  parsed = mapValues(parsed, parseValues) // parse numbers and booleans
  if (typeof parsed.location === 'number')
    parsed.location = String(parsed.location)
  if (typeof parsed.type === 'string' && !validListingTypes.has(parsed.type))
    parsed.type = null
  if (typeof parsed.country === 'string' && !validCountries.has(parsed.country))
    parsed.country = null
  // Drop date params that aren't strict YYYY-MM-DD; bots and ad creatives
  // sometimes leave template tokens unsubstituted, which crash GraphQL date
  // coercion server-side.
  dateFilters.forEach((key) => {
    if (parsed[key] !== undefined && !isValidIsoDate(parsed[key])) {
      parsed[key] = null
    }
  })
  // Drop numeric params that didn't parse to a number; privacy scrubbers can
  // replace values with sentinels like "_removed_", which crash GraphQL Int
  // coercion.
  numericFilters.forEach((key) => {
    if (parsed[key] !== undefined && typeof parsed[key] !== 'number') {
      parsed[key] = null
    }
  })
  parsed = omitBy(parsed, isNil) // remove null values
  return parsed
}
